import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Plus, Search, X, Pencil, Minus, ChevronRight, FileSpreadsheet, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import { Button, Field, Spinner } from "../components/ui/kit";
import { fmtKc, fmtPct } from "../lib/format";
import { barvaOsoby, inicialy } from "../lib/osoby";
import api, { apiError, souborUrl } from "../lib/api";
import { useUrlStav } from "../lib/navigace";
import Segment from "../components/Segment";
import Vyber from "../components/Vyber";
import CenikDodavatele from "../components/CenikDodavatele";
import NahratObrazek from "../components/NahratObrazek";

/**
 * Ceník podle prototypu 12b: technologie → řada výrobců → jeden seznam
 * rozdělený na zařízení / příslušenství / práce / dopravu.
 *
 * Filtry se zužují navzájem. Po výběru technologie zůstanou jen výrobci,
 * kteří v ní něco mají; po výběru výrobce jen technologie, ve kterých má
 * položky. Dvojice (výrobce × kategorie) posílá `/api/polozky/souhrn`.
 */

const TYP_LABEL = { MATERIAL: "Materiál", PRACE: "Práce", DOPRAVA: "Doprava" };
const MJ = ["ks", "m", "m2", "h", "km", "sada", "kpl"];

// Zařízení = hlavní položka (priorita A) typu materiál. Montáž s prioritou A
// je hlavní práce, ne zařízení.
const SEKCE = [
  { klic: "zarizeni", titul: "Zařízení", sedi: (p) => p.typ === "MATERIAL" && p.priority === "A" },
  { klic: "prislusenstvi", titul: "Příslušenství a materiál", sedi: (p) => p.typ === "MATERIAL" && p.priority !== "A" },
  { klic: "prace", titul: "Práce", sedi: (p) => p.typ === "PRACE" },
  { klic: "doprava", titul: "Doprava", sedi: (p) => p.typ === "DOPRAVA" },
];

// Význam položky (pole `priority`): A hlavní zařízení / B příslušenství / C drobný materiál.
const VYZNAM = [
  { klic: "A", titul: "Hlavní zařízení" },
  { klic: "B", titul: "Příslušenství" },
  { klic: "C", titul: "Drobný materiál" },
];

const ZDROJ_CENY = { RUCNE: "Ruční úprava", IMPORT_NABIDKY: "Import nabídky", CENIK_DODAVATELE: "Ceník dodavatele",
  SKLAD: "Ze skladu", VRACENI: "Vrácený import", NABIDKA: "Z nabídky" };
const pctText = (n) => (n == null ? "" : `${n > 0 ? "+" : ""}${String(n).replace(".", ",")} %`);
// Změna nákupní ceny za posledních 30 dní — u řádku ceníku malá šipka.
const cerstvaZmena = (p) => {
  const z = p.cena_zmena;
  return z && z.pct != null && Date.now() - new Date(z.kdy).getTime() < 30 * 864e5 ? z : null;
};

/** Historie nákupní ceny a kolik položky leží na skladě (skladová položka bere cenu z ceníku). */
function HistorieCen({ pid }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/api/polozky/${pid}/historie-cen`).then((r) => setD(r.data)).catch(() => setD({ items: [], sklad: [] })); }, [pid]);
  if (!d) return null;
  return (
    <div className="border-t border-line-softer pt-4" data-testid="historie-cen">
      <div className="label-up text-muted mb-1.5">Historie nákupní ceny</div>
      {!d.items.length && <p className="t-meta text-muted">Cena se zatím nezměnila.</p>}
      {d.items.slice(0, 12).map((h) => (
        <div key={h.id} className="flex items-center gap-3 py-1.5">
          <span className="min-w-0 flex-1">
            <span className="block t-meta font-600 text-ink truncate">{ZDROJ_CENY[h.zdroj] || h.zdroj}{h.dodavatel ? ` · ${h.dodavatel}` : ""}</span>
            <span className="block t-meta text-muted truncate">{new Date(h.created_at).toLocaleDateString("cs-CZ")}{h.kdo ? ` · ${h.kdo}` : ""}</span>
          </span>
          <span className="amount t-meta text-sec whitespace-nowrap">{fmtKc(h.cena_puvodni)} → <b className="text-ink">{fmtKc(h.cena_nova)}</b></span>
          <span className={`amount t-meta font-700 w-[58px] text-right ${h.rozdil_pct > 0 ? "text-danger" : "text-success"}`}>{pctText(h.rozdil_pct)}</span>
        </div>
      ))}
      {d.sklad.length > 0 && (
        <p className="t-meta text-sec mt-2">Na skladě: {d.sklad.map((x) => `${x.zasoba} ${x.mj}${d.sklad.length > 1 ? ` (${x.nazev})` : ""}`).join(", ")} — sklad bere tuhle cenu.</p>
      )}
    </div>
  );
}

// Oficiální prodejní cena má přednost; jinak nákup + marže. Nákup může být neznámý (null).
const prodej = (p) => (p.cena_prodej != null ? Number(p.cena_prodej)
  : Math.round((Number(p.cena_nakup) || 0) * (1 + (Number(p.vychozi_marze_pct) || 0) / 100)));
const marzeZProdeje = (nakup, prodejKc) => (Number(nakup) > 0 && prodejKc != null
  ? Math.round((Number(prodejKc) / Number(nakup) - 1) * 1000) / 10 : null);
// Sleva = o kolik je nákup levnější než prodej, v % z prodeje (sleva 30 % = marže 42,9 %).
const slevaZCen = (nakup, prodejKc) => (Number(prodejKc) > 0 && nakup != null && nakup !== ""
  ? Math.round(((Number(prodejKc) - Number(nakup)) / Number(prodejKc)) * 1000) / 10 : null);
const cislo = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
const pctPole = (v) => (v == null ? "" : String(v).replace(".", ","));
const kc2 = (v) => String(Math.round(v * 100) / 100);
const pocet = (n, j, d, p) => `${n} ${n === 1 ? j : n >= 2 && n <= 4 ? d : p}`;

function PolozkaForm({ polozka, kategorie, vyrobci, vychozi, onClose, onSaved, onDeleted }) {
  const editing = !!polozka;
  const [f, setF] = useState({
    name: polozka?.name || "", typ: polozka?.typ || "MATERIAL", mj: polozka?.mj || "ks",
    kategorie_id: polozka?.kategorie_id || vychozi?.kategorie_id || kategorie[0]?.id || "",
    vyrobce_id: polozka?.vyrobce_id || vychozi?.vyrobce_id || "",
    katalogove_cislo: polozka?.katalogove_cislo || "", ean: polozka?.ean || "",
    cena_nakup: polozka ? (polozka.cena_nakup ?? "") : "", cena_prodej: polozka?.cena_prodej ?? "",
    vychozi_marze_pct: polozka?.vychozi_marze_pct ?? 0,
    vychozi_dph_pct: polozka?.vychozi_dph_pct ?? 21,
    priority: polozka?.priority || "B", jen_servis: !!polozka?.jen_servis, dodavatel: polozka?.dodavatel || "",
  });
  // Hlavní zařízení (A): fotka a parametry do PDF nabídky. Fotka se ukládá zvlášť.
  const [parametry, setParametry] = useState(polozka?.parametry || []);
  const [obrazek, setObrazek] = useState(null);       // data URI; null = nenačteno / bez fotky
  const [obrazekZmenen, setObrazekZmenen] = useState(false);
  useEffect(() => {
    if (polozka?.ma_obrazek) api.get(`/api/polozky/${polozka.id}/obrazek`).then(({ data }) => setObrazek(data.obrazek)).catch(() => {});
  }, [polozka]);
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  // Sleva a marže se dopočítávají z cen; napsaná sleva změní nákup (prodej zůstane),
  // napsaná marže změní prodej (nákup zůstane). Bez prodejní ceny je marže výchozí
  // marží položky. `pis` drží text pole, do kterého se právě píše.
  const [pis, setPis] = useState(null);
  const nakupF = cislo(f.cena_nakup), prodejF = cislo(f.cena_prodej);
  const slevaVal = pis?.pole === "sleva" ? pis.text : pctPole(slevaZCen(nakupF, prodejF));
  const marzeVal = pis?.pole === "marze" ? pis.text
    : prodejF != null ? pctPole(marzeZProdeje(nakupF, prodejF))
      : (cislo(f.vychozi_marze_pct) == null ? "" : fmtPct(cislo(f.vychozi_marze_pct)));
  const pisSlevu = (t) => {
    setPis({ pole: "sleva", text: t });
    const s = cislo(t);
    if (s != null && s >= 0 && s < 100 && prodejF > 0) setF((x) => ({ ...x, cena_nakup: kc2(prodejF * (1 - s / 100)) }));
  };
  const pisMarzi = (t) => {
    setPis({ pole: "marze", text: t });
    const m = cislo(t);
    if (m == null || m <= -100) return;
    if (prodejF != null && nakupF > 0) setF((x) => ({ ...x, cena_prodej: kc2(nakupF * (1 + m / 100)) }));
    else setF((x) => ({ ...x, vychozi_marze_pct: t }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return setErr("Zadej název.");
    const nakup = cislo(f.cena_nakup), prodejKc = cislo(f.cena_prodej);
    if (nakup == null && prodejKc == null) return setErr("Zadej nákup, nebo prodejní cenu.");
    setSaving(true);
    // S prodejní cenou se marže dopočte z nákupu (bez nákupu je 0, dokud se nedoplní).
    const marze = prodejKc != null ? (marzeZProdeje(nakup, prodejKc) ?? 0) : cislo(f.vychozi_marze_pct) || 0;
    const payload = { ...f, vyrobce_id: f.vyrobce_id || null, cena_nakup: nakup, cena_prodej: prodejKc,
      vychozi_marze_pct: marze, vychozi_dph_pct: Number(f.vychozi_dph_pct) || 21,
      parametry: parametry.filter((x) => x.nazev.trim() && x.hodnota.trim()) };
    try {
      const pid = editing ? polozka.id : (await api.post("/api/polozky", payload)).data.id;
      if (editing) await api.patch(`/api/polozky/${pid}`, payload);
      if (obrazekZmenen) await api.put(`/api/polozky/${pid}/obrazek`, { obrazek: obrazek || "" });
      onSaved(); onClose();
    } catch (e2) { setErr(apiError(e2.response?.data?.detail)); } finally { setSaving(false); }
  };
  // Mazání je měkké — nabídky, které položku už mají v řádcích, zůstávají beze změny.
  const smazat = async () => {
    if (!window.confirm(`Smazat položku „${polozka.name}" z ceníku?`)) return;
    setSaving(true); setErr("");
    try { await api.delete(`/api/polozky/${polozka.id}`); onDeleted?.(polozka.id); onClose(); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="polozka-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[520px] md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="text-[16px] font-800">{editing ? "Upravit položku" : "Nová položka"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Název"><input className="field" value={f.name} onChange={up("name")} data-testid="polozka-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Typ">
              <Vyber className="field" value={f.typ} onChange={up("typ")} data-testid="polozka-typ">
                {Object.entries(TYP_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Vyber>
            </Field>
            <Field label="Technologie">
              <Vyber className="field" value={f.kategorie_id} onChange={up("kategorie_id")}>
                {kategorie.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Vyber>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Výrobce">
              <Vyber className="field" value={f.vyrobce_id} onChange={up("vyrobce_id")} data-testid="polozka-vyrobce">
                <option value="">—</option>
                {vyrobci.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Vyber>
            </Field>
            <Field label="Význam" hint="A = hlavní zařízení nebo hlavní práce">
              <Vyber className="field" value={f.priority} onChange={up("priority")} data-testid="polozka-priorita">
                <option value="A">A · zařízení / hlavní</option>
                <option value="B">B · příslušenství</option>
                <option value="C">C · drobný materiál</option>
              </Vyber>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nákup (Kč)">
              <input type="number" min="0" step="any" inputMode="decimal" className="field amount" placeholder="neznámý"
                value={f.cena_nakup} onChange={up("cena_nakup")} data-testid="polozka-nakup" />
            </Field>
            <Field label="Prodejní cena (Kč)">
              <input type="number" min="0" step="any" inputMode="decimal" className="field amount" placeholder="nákup + marže"
                value={f.cena_prodej} onChange={up("cena_prodej")} data-testid="polozka-prodej" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sleva %" hint="Z prodeje → nákup">
              <input type="text" inputMode="decimal" className="field amount" placeholder={prodejF > 0 ? "0" : "zadej prodej"} disabled={!(prodejF > 0)}
                value={slevaVal} onChange={(e) => pisSlevu(e.target.value.replace(/[^\d.,]/g, ""))} onBlur={() => setPis(null)} data-testid="polozka-sleva" />
            </Field>
            <Field label="Marže %" hint={prodejF != null ? "Z nákupu → prodej" : "Výchozí marže položky"}>
              <input type="text" inputMode="decimal" className="field amount" disabled={prodejF != null && !(nakupF > 0)}
                value={marzeVal} onChange={(e) => pisMarzi(e.target.value.replace(/[^\d.,-]/g, ""))} onBlur={() => setPis(null)} data-testid="polozka-marze" />
            </Field>
          </div>
          <Field label="MJ">
            <Vyber className="field" value={f.mj} onChange={up("mj")}>{MJ.map((m) => <option key={m}>{m}</option>)}</Vyber>
          </Field>
          {cislo(f.cena_prodej) != null && (
            <p className="t-meta text-muted -mt-1" data-testid="polozka-prodej-info">
              {cislo(f.cena_nakup) == null
                ? "Nákup neznámý — v nabídce počítáme marži 0, dokud ho nedoplníš (tady nebo v řádku nabídky)."
                : "Nabídka prodává přesně za prodejní cenu, marže se dopočte z nákupu."}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Katalog. číslo"><input className="field amount" value={f.katalogove_cislo} onChange={up("katalogove_cislo")} /></Field>
            <Field label="EAN"><input className="field amount" value={f.ean} onChange={up("ean")} /></Field>
          </div>
          <Field label="Dodavatel" hint="Od koho je nákupní cena">
            <input className="field" value={f.dodavatel} maxLength={120} onChange={up("dodavatel")} data-testid="polozka-dodavatel" />
          </Field>
          <Field label="Výchozí DPH">
            <Vyber className="field" value={f.vychozi_dph_pct} onChange={up("vychozi_dph_pct")} data-testid="polozka-dph">
              <option value={21}>21 %</option>
              <option value={12}>12 %</option>
            </Vyber>
          </Field>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="polozka-jen-servis">
            <input type="checkbox" className="h-5 w-5 mt-0.5 shrink-0 accent-ink" checked={f.jen_servis}
              onChange={(e) => setF({ ...f, jen_servis: e.target.checked })} />
            <span>
              <span className="block t-telo font-600 text-ink">Jen pro servis</span>
              <span className="block t-meta text-muted">Model z převodu starých dat. Neukáže se při výběru do nabídky ani při importu.</span>
            </span>
          </label>
          {f.priority === "A" && (
            <div className="rounded-[14px] bg-surface p-3.5 flex flex-col gap-3" data-testid="polozka-pdf">
              <div>
                <div className="t-telo font-700 text-ink">Do PDF nabídky</div>
                <p className="t-meta text-muted">Hlavní zařízení se v nabídce ukáže s fotkou a parametry.</p>
              </div>
              <NahratObrazek tvar="produkt" value={obrazek} testid="polozka-obrazek"
                onChange={(v) => { setObrazek(v || null); setObrazekZmenen(true); }} />
              <div className="flex flex-col gap-1.5" data-testid="polozka-parametry">
                {parametry.map((x, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-1.5">
                    <input className="field min-w-0" value={x.nazev} maxLength={40} placeholder="Výkon"
                      onChange={(e) => setParametry(parametry.map((y, j) => (j === i ? { ...y, nazev: e.target.value } : y)))} data-testid={`parametr-nazev-${i}`} />
                    <input className="field min-w-0" value={x.hodnota} maxLength={40} placeholder="8 kW"
                      onChange={(e) => setParametry(parametry.map((y, j) => (j === i ? { ...y, hodnota: e.target.value } : y)))} data-testid={`parametr-hodnota-${i}`} />
                    <button type="button" onClick={() => setParametry(parametry.filter((_, j) => j !== i))} aria-label="Odebrat parametr"
                      className="tap h-11 w-11 md:h-10 md:w-10 rounded-full inline-flex items-center justify-center text-muted hover:text-danger"><X size={16} /></button>
                  </div>
                ))}
                {parametry.length < 6 && (
                  <button type="button" onClick={() => setParametry([...parametry, { nazev: "", hodnota: "" }])} data-testid="parametr-pridat"
                    className="tap md:min-h-0 self-start inline-flex items-center gap-1 t-meta font-700 text-accent"><Plus size={15} strokeWidth={2.4} /> Přidat parametr</button>
                )}
              </div>
            </div>
          )}
          {editing && <HistorieCen pid={polozka.id} />}
          {err && <div className="text-[12.5px] text-danger">{err}</div>}
          <div className="flex items-center gap-2">
            {editing && (
              <button type="button" onClick={smazat} disabled={saving} data-testid="polozka-smazat"
                className="t-meta font-600 text-danger hover:underline">Smazat položku</button>
            )}
            <button type="button" onClick={onClose} className="btn-ghost ml-auto">Zrušit</button>
            <Button disabled={saving} data-testid="polozka-save">{saving ? <Spinner /> : "Uložit"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Kruh výrobce: logo, když je; jinak iniciály v barvě odvozené z id.
 * Výřez loga (`logo_zoom`, `logo_x/y`) je jen CSS transformace nad
 * původním obrázkem — uložený soubor se nikdy nemění.
 */
export function LogoVyrobce({ v, size = 64, zoom, x, y }) {
  const url = souborUrl(v.logo_url);
  const z = zoom ?? v.logo_zoom ?? 1;
  const px = (x ?? v.logo_x ?? 0) * size;
  const py = (y ?? v.logo_y ?? 0) * size;
  if (url) {
    return (
      <span className="rounded-full bg-white border border-line-softer overflow-hidden flex items-center justify-center"
        style={{ width: size, height: size }}>
        <img src={url} alt={v.name} draggable={false} className="object-contain select-none pointer-events-none"
          style={{ width: size - 8, height: size - 8, transform: `translate(${px}px, ${py}px) scale(${z})` }} />
      </span>
    );
  }
  return (
    <span className="rounded-full text-white font-700 flex items-center justify-center"
      style={{ width: size, height: size, background: barvaOsoby(v.id), fontSize: Math.round(size * 0.27) }}>
      {inicialy(v.name)}
    </span>
  );
}

/** Nový výrobce nebo úprava: název, web a logo. */
function VyrobceForm({ vyrobce, kategorie = [], vychoziTechnologie = null, onClose, onSaved, onDeleted }) {
  const editing = !!vyrobce;
  const [name, setName] = useState(vyrobce?.name || "");
  const [url, setUrl] = useState(vyrobce?.url || "");
  // Technologie, které výrobce dodává — nabídne se u nich i bez položek v ceníku.
  const [technologie, setTechnologie] = useState(vyrobce?.technologie_ids || (vychoziTechnologie ? [vychoziTechnologie] : []));
  const prepniTech = (id) => setTechnologie((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));
  const [soubor, setSoubor] = useState(null);
  const [nahled, setNahled] = useState(souborUrl(vyrobce?.logo_url));
  // Výřez: zoom 0,3–4, posun v podílu průměru kruhu (−1…1).
  const [zoom, setZoom] = useState(vyrobce?.logo_zoom ?? 1);
  const [pos, setPos] = useState({ x: vyrobce?.logo_x ?? 0, y: vyrobce?.logo_y ?? 0 });
  const [err, setErr] = useState(""); const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const tah = useRef(null);
  const NAHLED = 140;

  const vyberLogo = (f) => {
    if (!f) return;
    setSoubor(f);
    setNahled(URL.createObjectURL(f));
    setZoom(1); setPos({ x: 0, y: 0 });     // nový obrázek, starý výřez by neseděl
  };
  const omez = (n) => Math.max(-1, Math.min(1, n));
  // Posun tahem prstu / myši po náhledu; přepočet na podíl průměru.
  const tahStart = (e) => { tah.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const tahPohyb = (e) => {
    if (!tah.current) return;
    setPos({ x: omez(tah.current.px + (e.clientX - tah.current.x) / NAHLED),
      y: omez(tah.current.py + (e.clientY - tah.current.y) / NAHLED) });
  };
  const tahKonec = () => { tah.current = null; };
  const priblizit = (d) => setZoom((z) => Math.round(Math.max(0.3, Math.min(4, z + d)) * 100) / 100);
  const smazat = async () => {
    const n = vyrobce.pocet || 0;
    const zprava = n
      ? `Smazat výrobce „${vyrobce.name}"? ${n} ${n === 1 ? "položka zůstane" : n < 5 ? "položky zůstanou" : "položek zůstane"} v ceníku bez výrobce.`
      : `Smazat výrobce „${vyrobce.name}"?`;
    if (!window.confirm(zprava)) return;
    setSaving(true); setErr("");
    try { await api.delete(`/api/vyrobci/${vyrobce.id}`); onDeleted?.(vyrobce.id); onClose(); }
    catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); setSaving(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setErr("Zadej název výrobce.");
    setSaving(true); setErr("");
    try {
      let id = vyrobce?.id;
      const telo = { name: name.trim(), url: url.trim() || null, technologie_ids: technologie };
      if (editing) await api.patch(`/api/vyrobci/${id}`, telo);
      else id = (await api.post("/api/vyrobci", telo)).data.id;
      if (soubor) {
        const fd = new FormData(); fd.append("file", soubor);
        await api.post(`/api/vyrobci/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if (nahled) {
        await api.patch(`/api/vyrobci/${id}`, { logo_zoom: zoom, logo_x: Math.round(pos.x * 100) / 100, logo_y: Math.round(pos.y * 100) / 100 });
      }
      onSaved(id); onClose();
    } catch (e2) { setErr(apiError(e2.response?.data?.detail) || e2.message); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid="vyrobce-form">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full md:max-w-[440px] md:rounded-card rounded-t-sheet shadow-panel">
        <div className="border-b border-line-softer px-6 py-4 flex items-center justify-between">
          <h2 className="text-[16px] font-800">{editing ? "Upravit výrobce" : "Nový výrobce"}</h2>
          <button onClick={onClose} className="text-muted hover:text-ink"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {/* Logo a jeho výřez: náhled je stejný kruh jako v řadě, jen větší.
              Tahem se posouvá, posuvníkem přibližuje. */}
          <div className="flex items-start gap-4">
            {nahled ? (
              <div onPointerDown={tahStart} onPointerMove={tahPohyb} onPointerUp={tahKonec} onPointerCancel={tahKonec}
                data-testid="vyrobce-logo-nahled" title="Tahem posuneš logo v kruhu"
                className="shrink-0 rounded-full border border-line-strong bg-white overflow-hidden touch-none cursor-grab active:cursor-grabbing"
                style={{ width: NAHLED, height: NAHLED }}>
                <img src={nahled} alt="" draggable={false} className="object-contain select-none pointer-events-none"
                  style={{ width: NAHLED - 8, height: NAHLED - 8, margin: 4,
                    transform: `translate(${pos.x * NAHLED}px, ${pos.y * NAHLED}px) scale(${zoom})` }} />
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="vyrobce-logo"
                className="tap shrink-0 rounded-full border-[1.5px] border-dashed border-line-strong bg-inputbg
                  flex items-center justify-center hover:border-accent-hover t-meta text-muted"
                style={{ width: NAHLED, height: NAHLED }}>
                Nahrát logo
              </button>
            )}
            <div className="flex-1 min-w-0 space-y-3">
              <div className="t-meta text-muted">
                PNG, JPG nebo SVG do 5 MB. Zobrazí se v řadě výrobců nad ceníkem.
              </div>
              {nahled && (
                <>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => priblizit(-0.1)} data-testid="logo-oddalit" title="Oddálit"
                      className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg text-sec flex items-center justify-center"><Minus size={13} /></button>
                    <input type="range" min="0.3" max="4" step="0.05" value={zoom} data-testid="logo-zoom"
                      onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-accent" aria-label="Přiblížení loga" />
                    <button type="button" onClick={() => priblizit(0.1)} data-testid="logo-priblizit" title="Přiblížit"
                      className="tap md:min-h-0 md:min-w-0 h-8 w-8 rounded-full bg-inputbg text-sec flex items-center justify-center"><Plus size={13} /></button>
                    <span className="amount t-meta text-sec w-[44px] text-right">{Math.round(zoom * 100)} %</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 t-meta font-600">
                    <button type="button" onClick={() => { setZoom(1); setPos({ x: 0, y: 0 }); }} className="text-sec hover:text-ink">Vycentrovat</button>
                    <button type="button" onClick={() => fileRef.current?.click()} className="text-accent-hover">Vyměnit logo</button>
                  </div>
                  <div className="t-meta text-muted-2">Tahem v kruhu logo posuneš.</div>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { vyberLogo(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
          <Field label="Název"><input className="field" value={name} onChange={(e) => setName(e.target.value)} data-testid="vyrobce-name" placeholder="např. Viessmann" /></Field>
          <Field label="Web"><input className="field" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></Field>
          {kategorie.length > 0 && (
            <div data-testid="vyrobce-technologie">
              <span className="label-up text-muted block mb-1.5">Dodává technologie</span>
              <div className="flex flex-wrap gap-1.5">
                {kategorie.map((k) => {
                  const on = technologie.includes(k.id);
                  return (
                    <button key={k.id} type="button" onClick={() => prepniTech(k.id)} aria-pressed={on} data-testid={`vyrobce-tech-${k.id}`}
                      className={`tap md:min-h-0 pill h-10 md:h-8 px-3 border t-meta font-600 transition-colors ${
                        on ? "bg-ink border-ink text-white" : "bg-white border-line-strong text-sec hover:bg-inputbg"}`}>{k.name}</button>
                  );
                })}
              </div>
              <p className="t-meta text-muted mt-1.5">U těchto technologií se výrobce nabídne vždy — i když v ceníku zatím nemá položky.</p>
            </div>
          )}
          {err && <div className="text-[12.5px] text-danger">{err}</div>}
          <div className="flex items-center gap-2">
            {editing && (
              <button type="button" onClick={smazat} disabled={saving} data-testid="vyrobce-smazat"
                className="t-meta font-600 text-danger hover:underline">Smazat výrobce</button>
            )}
            <button type="button" onClick={onClose} className="btn-ghost ml-auto">Zrušit</button>
            <Button disabled={saving} data-testid="vyrobce-save">{saving ? <Spinner /> : "Uložit"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Polozky() {
  const [items, setItems] = useState(null);
  const [kategorie, setKategorie] = useState([]);
  const [souhrn, setSouhrn] = useState(null);
  // Filtry v adrese — po návratu z karty zařízení zůstane ceník tak, jak byl.
  const [q, setQ] = useUrlStav("q", "");
  const [kat, setKat] = useUrlStav("technologie");
  const [vyr, setVyr] = useUrlStav("vyrobce");
  const [vyznam, setVyznam] = useUrlStav("vyznam");   // null = vše | A | B | C
  const [servis, setServis] = useUrlStav("servis");   // null = bez modelů jen pro servis | "jen"
  const nav = useNavigate();
  const [form, setForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [vyrobceForm, setVyrobceForm] = useState(null);   // false | null | {vyrobce}
  const [dodavatelImport, setDodavatelImport] = useState(false);
  const [chyba, setChyba] = useState("");
  // Hromadná úprava: označené položky (id → true).
  const [vybrane, setVybrane] = useState({});

  const vyrobci = useMemo(() => souhrn?.vyrobci || [], [souhrn]);
  const vyrMap = useMemo(() => Object.fromEntries(vyrobci.map((v) => [v.id, v])), [vyrobci]);
  const katMap = useMemo(() => Object.fromEntries(kategorie.map((k) => [k.id, k])), [kategorie]);

  const loadSouhrn = useCallback(() => api.get("/api/polozky/souhrn").then((r) => setSouhrn(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    const { data } = await api.get("/api/polozky", { params: { q, kategorie_id: kat, vyrobce_id: vyr, limit: 500,
      servis: servis === "jen" ? "jen" : "bez" } });
    setItems(data.items);
  }, [q, kat, vyr, servis]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    api.get("/api/meta/ciselniky").then((r) => setKategorie(r.data.kategorie || []));
    loadSouhrn();
  }, [loadSouhrn]);
  const poZmene = () => { load(); loadSouhrn(); };
  const vybraneIds = Object.keys(vybrane).filter((k) => vybrane[k]);
  const prepniVyber = (id) => setVybrane((v) => ({ ...v, [id]: !v[id] }));

  // Vzájemné zúžení: technologie ⇄ výrobci.
  const vybranyVyrobce = vyr ? vyrMap[vyr] : null;
  const kategorieViditelne = vybranyVyrobce
    ? kategorie.filter((k) => vybranyVyrobce.kategorie_ids.includes(k.id))
    : kategorie.filter((k) => (souhrn?.kategorie_pocty?.[k.id] || 0) > 0 || !souhrn);
  const vyrobciViditelni = kat ? vyrobci.filter((v) => v.kategorie_ids.includes(kat)) : vyrobci;

  const vyberKat = (id) => {
    setKat(id);
    // Výrobce, který v nové technologii nic nemá, ztrácí smysl — pustit ho.
    if (id && vybranyVyrobce && !vybranyVyrobce.kategorie_ids.includes(id)) setVyr(null);
  };
  const vyberVyr = (id) => setVyr(vyr === id ? null : id);

  // Marže −/+ přímo na řádku (12b). Nula je platná hodnota — nepřepisovat.
  const upravMarzi = async (p, delta) => {
    // Krok o 1 % a zaokrouhlit na desetiny (přesná marže z importu má víc desetin).
    const nova = Math.max(0, Math.round(((Number(p.vychozi_marze_pct) || 0) + delta) * 10) / 10);
    setItems((xs) => xs.map((x) => (x.id === p.id ? { ...x, vychozi_marze_pct: nova } : x)));
    try { await api.patch(`/api/polozky/${p.id}`, { vychozi_marze_pct: nova }); }
    catch (e) { setChyba(apiError(e.response?.data?.detail) || e.message); load(); }
  };

  const pocetVyznam = useMemo(() => (items || []).reduce((a, p) => { a[p.priority || "B"] = (a[p.priority || "B"] || 0) + 1; return a; }, {}), [items]);
  const filtrovane = useMemo(() => (items || []).filter((p) => !vyznam || (p.priority || "B") === vyznam), [items, vyznam]);
  const sekce = useMemo(() => SEKCE.map((s) => ({ ...s, polozky: filtrovane.filter(s.sedi) })).filter((s) => s.polozky.length), [filtrovane]);
  // Hlavní zařízení (A, materiál) má vlastní kartu: klienti, kdo ho mají, a šablony nabídek.
  const jeZarizeni = (p) => p.typ === "MATERIAL" && p.priority === "A";
  const otevri = (p) => { if (jeZarizeni(p)) nav(`/cenik/zarizeni/${p.id}`); else { setEditItem(p); setForm(true); } };
  const titul = vybranyVyrobce ? `${vybranyVyrobce.name}${kat ? ` · ${katMap[kat]?.name || ""}` : ""}`
    : kat ? (katMap[kat]?.name || "") : "Všechny položky";
  const podtitul = souhrn ? `${pocet(souhrn.celkem, "položka", "položky", "položek")} u ${pocet(vyrobci.length, "výrobce", "výrobců", "výrobců")}` : "";

  const pilulka = (aktivni) =>
    `filtr-seg transition-colors ${
      aktivni ? "text-white" : "text-sec hover:text-ink"}`;

  return (
    <div data-testid="polozky-page">
      <PageHeader eyebrow="Obchod / Ceník" title="Ceník" subtitle={podtitul || "Materiál, práce a doprava pro nabídky."}
        actions={
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 bg-white rounded-pill px-4 h-11 md:h-10 flex-1 min-w-0 md:flex-none md:w-[250px]">
              <Search size={14} className="text-muted shrink-0" />
              <input className="bg-transparent outline-none w-full min-w-0 text-[16px] md:text-[12.5px]" placeholder="Hledat název, EAN, kat. číslo…"
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="polozky-search" />
            </div>
            <Button variant="ghost" onClick={() => setDodavatelImport(true)} data-testid="cenik-dodavatele-btn" className="shrink-0" aria-label="Ceník dodavatele"
              title="Nahrát ceník od dodavatele a porovnat ceny">
              <FileSpreadsheet size={16} /><span className="hidden md:inline">Ceník dodavatele</span>
            </Button>
            <Button onClick={() => { setEditItem(null); setForm(true); }} data-testid="new-polozka" className="shrink-0" aria-label="Nová položka"><Plus size={16} /><span className="hidden sm:inline"> Nová položka</span></Button>
          </div>
        } />

      {/* technologie — pilulky v jednom pásu; po výběru výrobce jen ty, kde má položky */}
      <Segment className="filtr-pas mb-3.5" data-testid="polozky-technologie">
        <button onClick={() => vyberKat(null)} data-on={!kat} className={pilulka(!kat)} data-testid="kat-vse">Vše</button>
        {kategorieViditelne.map((k) => (
          <button key={k.id} onClick={() => vyberKat(kat === k.id ? null : k.id)} data-on={kat === k.id} className={pilulka(kat === k.id)} data-testid={`kat-${k.id}`}>
            {k.name}
          </button>
        ))}
      </Segment>

      {/* výrobci — kruhová loga; po výběru technologie jen ti, kdo v ní něco mají */}
      {/* „Přidat“ je první a při posouvání zůstává vidět — u desítek výrobců
          by na konci řádku zmizelo za okrajem. Okraj vpravo mizí do ztracena,
          ať je jasné, že řádek pokračuje. */}
      <div className="flex gap-2.5 items-start mb-3 -mx-4 px-4 md:mx-0 md:px-0" data-testid="polozky-vyrobci">
        <button onClick={() => setVyrobceForm({})} data-testid="vyrobce-novy"
          className="tap sticky left-0 z-[1] bg-surface pr-1 flex flex-col items-center gap-1.5 w-[80px] shrink-0 text-muted hover:text-accent-hover">
          <span className="h-16 w-16 rounded-full border-[1.6px] border-dashed border-line-strong flex items-center justify-center text-[24px] font-400">+</span>
          <span className="text-[12px] font-500">Přidat</span>
        </button>
        <div className="flex gap-2.5 overflow-x-auto p-1 -m-1 min-w-0 flex-1 [scrollbar-width:none] [mask-image:linear-gradient(90deg,#000_90%,transparent)]">
        {vyrobciViditelni.map((v) => {
          const aktivni = vyr === v.id;
          return (
            <div key={v.id} className="relative flex flex-col items-center gap-1.5 w-[88px] shrink-0">
              <button onClick={() => vyberVyr(v.id)} data-testid={`vyrobce-${v.id}`} title={`${v.name} · ${pocet(v.pocet, "položka", "položky", "položek")}`}
                className={`tap rounded-full ${aktivni ? "ring-2 ring-ink ring-offset-2 ring-offset-surface" : ""}`}>
                <LogoVyrobce v={v} size={64} />
              </button>
              {aktivni && (
                <button onClick={() => setVyrobceForm({ vyrobce: v })} data-testid={`vyrobce-upravit-${v.id}`} title="Upravit výrobce a logo"
                  className="absolute right-1 top-[44px] h-6 w-6 rounded-full bg-white shadow-panel flex items-center justify-center text-sec hover:text-accent-hover">
                  <Pencil size={12} />
                </button>
              )}
              <span className={`text-[12px] truncate max-w-[88px] ${aktivni ? "font-700 text-ink" : "font-500 text-sec"}`}>{v.name}</span>
            </div>
          );
        })}
        {!vyrobciViditelni.length && vyrobci.length > 0 && (
          <span className="self-center t-meta text-muted">V téhle technologii zatím žádný výrobce nemá položky.</span>
        )}
        </div>
      </div>

      {/* význam položky — hlavní zařízení vs. co se k němu přidává do nabídky */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="polozky-vyznam">
        <span className="label-up text-muted mr-1">Význam</span>
        {[{ klic: null, titul: "Vše" }, ...VYZNAM].map((v) => {
          const aktivni = vyznam === v.klic;
          const n = v.klic ? (pocetVyznam[v.klic] || 0) : (items?.length || 0);
          return (
            <button key={v.klic || "vse"} data-on={aktivni} onClick={() => setVyznam(v.klic)} data-testid={`vyznam-${v.klic || "vse"}`}
              className={`cip filtr ${
                aktivni ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
              {v.klic && <span className={`h-5 w-5 rounded-[6px] text-[10.5px] font-800 flex items-center justify-center ${
                aktivni ? "bg-accent text-white" : "bg-inputbg text-sec"}`}>{v.klic}</span>}
              {v.titul}<span className="filtr-pocet">{n}</span>
            </button>
          );
        })}
        {/* Modely převedené ze servisu (bez nákupu) jsou v ceníku zvlášť — do nabídek nepatří. */}
        {(souhrn?.jen_servis > 0 || servis === "jen") && (
          <button data-on={servis === "jen"} onClick={() => setServis(servis === "jen" ? null : "jen")} data-testid="polozky-jen-servis"
            title="Modely ze servisu (převod starých dat) — nenabízejí se do nabídek"
            className={`cip filtr md:ml-2 ${
              servis === "jen" ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
            Jen pro servis<span className="filtr-pocet">{souhrn?.jen_servis || 0}</span>
          </button>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 md:px-5 py-3.5 flex items-center gap-2.5 border-b border-line">
          {filtrovane.length > 0 && (
            <input type="checkbox" className="h-5 w-5 shrink-0 accent-ink cursor-pointer" aria-label="Označit všechny zobrazené položky"
              data-testid="polozky-vyber-vse" checked={filtrovane.every((p) => vybrane[p.id])}
              onChange={(e) => setVybrane(e.target.checked ? Object.fromEntries(filtrovane.map((p) => [p.id, true])) : {})} />
          )}
          <span className="t-sekce text-ink truncate">{titul}</span>
          <span className="amount text-[11px] text-muted-2">{items ? filtrovane.length : ""}</span>
          {vybranyVyrobce?.url && (
            <a href={vybranyVyrobce.url} target="_blank" rel="noreferrer" className="t-meta text-muted hover:text-ink truncate">
              {vybranyVyrobce.url.replace(/^https?:\/\//, "")}
            </a>
          )}
          <span className="ml-auto hidden md:inline t-meta text-muted">řazeno: zařízení → příslušenství → práce</span>
        </div>
        {items === null ? <div className="p-10 flex justify-center text-muted"><Spinner /></div>
          : filtrovane.length === 0 ? (
            <EmptyState icon="Package" title="Nic jsme nenašli"
              description={q || vyznam || servis ? "Zkus jiný název, výrobce nebo zruš filtry." : "Přidej první položku do ceníku."}
              action={<Button onClick={() => { setEditItem(null); setForm(true); }}><Plus size={16} /> Nová položka</Button>} />
          ) : (<>
            <div className="hidden md:flex items-center gap-3.5 px-5 py-2 bg-[#fbfaf8] border-b border-line-soft label-up text-muted-2">
              <span className="w-5" /><span className="w-10" /><span className="flex-1">Položka</span><span className="w-[84px] text-right">Nákup</span>
              <span className="w-[44px] text-right">DPH</span><span className="w-[104px] text-center" title="Marže je vaše, ne výrobce">Vaše marže</span>
              <span className="w-[96px] text-right">Prodej</span><span className="w-[52px]" />
            </div>
            {sekce.map((s) => (
              <React.Fragment key={s.klic}>
                <div className="px-4 md:px-5 py-2 bg-[#fbfaf8] border-b border-line-soft flex items-center gap-2" data-testid={`sekce-${s.klic}`}>
                  <span className="label-up text-muted">{s.titul}</span>
                  <span className="amount text-[11px] text-muted-2">{s.polozky.length}</span>
                </div>
                {s.polozky.map((p) => {
                  const v = vyrMap[p.vyrobce_id];
                  const barva = v ? barvaOsoby(v.id) : "#5c6169";
                  const sub = [jeZarizeni(p) ? katMap[p.kategorie_id]?.name : null, p.katalogove_cislo, p.mj,
                    !vyr && v ? v.name : null, p.typ !== "MATERIAL" ? TYP_LABEL[p.typ].toLowerCase() : null]
                    .filter(Boolean).join(" · ");
                  return (
                    <div key={p.id} onClick={() => otevri(p)} role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter") otevri(p); }}
                      data-testid={`polozka-row-${p.id}`}
                      className={`flex items-center gap-3 md:gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 cursor-pointer hover:bg-inputbg ${vybrane[p.id] ? "bg-[#fff7f2]" : ""}`}>
                      <label className="tap md:min-h-0 -m-2 p-2 shrink-0 flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="h-5 w-5 accent-ink cursor-pointer" checked={!!vybrane[p.id]} onChange={() => prepniVyber(p.id)}
                          aria-label={`Označit ${p.name}`} data-testid={`polozka-vyber-${p.id}`} />
                      </label>
                      {v ? (
                        // Položka s výrobcem nese jeho logo (bez loga iniciály výrobce v jeho barvě).
                        <span className="shrink-0"><LogoVyrobce v={v} size={40} /></span>
                      ) : (
                        <span className="h-10 w-10 rounded-[12px] flex items-center justify-center text-[11px] font-700 shrink-0"
                          style={{ background: `${barva}22`, color: barva }}>{inicialy(p.name)}</span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="t-telo font-600 text-ink truncate">{p.name}</span>
                          {p.jen_servis && <span className="shrink-0 inline-flex items-center h-5 px-2 rounded-pill bg-inputbg text-sec text-[9.5px] tracking-[0.07em] uppercase font-700" data-testid={`polozka-servis-${p.id}`}>Jen servis</span>}
                        </span>
                        <span className="block t-meta text-muted truncate amount">{sub}</span>
                      </span>
                      <span className="hidden md:block amount text-[12.5px] text-sec w-[84px] text-right">
                        {p.cena_nakup == null ? <span className="text-muted" title="Nákup neznámý" data-testid={`nakup-neznamy-${p.id}`}>—</span> : fmtKc(p.cena_nakup)}
                        {cerstvaZmena(p) && (
                          <span className={`block text-[10.5px] font-700 ${cerstvaZmena(p).pct > 0 ? "text-danger" : "text-success"}`}
                            title={`Nákup změněn ${new Date(cerstvaZmena(p).kdy).toLocaleDateString("cs-CZ")} z ${fmtKc(cerstvaZmena(p).z)} Kč`}
                            data-testid={`cena-zmena-${p.id}`}>
                            {cerstvaZmena(p).pct > 0 ? "↑" : "↓"} {String(Math.abs(cerstvaZmena(p).pct)).replace(".", ",")} %
                          </span>
                        )}
                      </span>
                      <span className="hidden md:block text-[12px] text-muted w-[44px] text-right">{p.vychozi_dph_pct} %</span>
                      {p.cena_prodej != null ? (
                        // Oficiální prodejní cena: marže se jen dopočte, tlačítka nedávají smysl.
                        <span className="hidden md:block w-[104px] text-center amount text-[12px] text-sec" title="Prodejní cena je pevná — marže z nákupu">
                          {marzeZProdeje(p.cena_nakup, p.cena_prodej) != null ? `${String(marzeZProdeje(p.cena_nakup, p.cena_prodej)).replace(".", ",")} %` : "—"}
                        </span>
                      ) : (
                      <span className="hidden md:flex w-[104px] items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => upravMarzi(p, -1)} data-testid={`marze-minus-${p.id}`} title="Snížit marži o 1 %"
                          className="h-6 w-6 rounded-full bg-inputbg text-sec hover:bg-line-2 flex items-center justify-center"><Minus size={12} /></button>
                        <span className="amount text-[12px] font-600 text-ink min-w-[34px] text-center whitespace-nowrap">{fmtPct(p.vychozi_marze_pct)} %</span>
                        <button onClick={() => upravMarzi(p, 1)} data-testid={`marze-plus-${p.id}`} title="Zvýšit marži o 1 %"
                          className="h-6 w-6 rounded-full bg-inputbg text-sec hover:bg-line-2 flex items-center justify-center"><Plus size={12} /></button>
                      </span>
                      )}
                      <span className="amount text-[13px] font-600 text-ink md:w-[96px] text-right shrink-0">
                        {fmtKc(prodej(p))}<span className="md:hidden text-muted font-500"> Kč</span>
                      </span>
                      {/* Hlavní zařízení se otevírá jako karta; tužka upraví samotnou položku. */}
                      {jeZarizeni(p) ? (
                        <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => { setEditItem(p); setForm(true); }} title="Upravit položku" data-testid={`polozka-upravit-${p.id}`}
                            className="tap h-8 w-8 rounded-full text-muted hover:text-ink hover:bg-inputbg flex items-center justify-center"><Pencil size={14} /></button>
                          <ChevronRight size={16} className="text-muted" />
                        </span>
                      ) : <span className="w-[1px] md:w-[52px] shrink-0" />}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </>)}
        {chyba && <div className="px-5 py-3 t-meta text-danger" data-testid="polozky-chyba">{chyba}</div>}
      </div>

      {vybraneIds.length > 0 && (
        <HromadnaListaCeniku ids={vybraneIds} celkem={filtrovane.length} vyrobci={vyrobci} kategorie={kategorie}
          onVse={() => setVybrane(Object.fromEntries(filtrovane.map((p) => [p.id, true])))}
          onZrusit={() => setVybrane({})} onHotovo={poZmene} />
      )}

      {form && <PolozkaForm polozka={editItem} kategorie={kategorie} vyrobci={vyrobci}
        vychozi={{ kategorie_id: kat, vyrobce_id: vyr }}
        onClose={() => setForm(false)} onSaved={poZmene} onDeleted={poZmene} />}
      {dodavatelImport && <CenikDodavatele onClose={() => setDodavatelImport(false)} onDone={poZmene} />}
      {vyrobceForm && <VyrobceForm vyrobce={vyrobceForm.vyrobce || null} kategorie={kategorie} vychoziTechnologie={kat}
        onClose={() => setVyrobceForm(null)} onSaved={(id) => { loadSouhrn(); if (!vyrobceForm.vyrobce) setVyr(id); }}
        onDeleted={(id) => { if (vyr === id) setVyr(null); poZmene(); }} />}
    </div>
  );
}


const cisloPct = (t) => {
  const v = Number(String(t).replace(/\s/g, "").replace(",", "."));
  return String(t).trim() !== "" && Number.isFinite(v) ? v : null;
};

/**
 * Hromadná úprava označených položek ceníku (např. ceník Loxone bez slevy):
 * sleva od dodavatele (prodej zůstane, nákup = prodej − sleva), marže (nákup
 * zůstane, prodej = nákup + marže), výrobce, technologie, typ a význam.
 */
function HromadnaListaCeniku({ ids, celkem, vyrobci, kategorie, onVse, onZrusit, onHotovo }) {
  const [sleva, setSleva] = useState("");
  const [marze, setMarze] = useState("");
  const [busy, setBusy] = useState(false);
  const [zprava, setZprava] = useState("");
  const proved = async (zmena) => {
    setBusy(true); setZprava("");
    try {
      const { data } = await api.post("/api/polozky/hromadne", { ids, ...zmena });
      const pres = data.preskoceno || [];
      setZprava(`Upraveno ${data.upraveno}.${pres.length ? ` Přeskočeno ${pres.length} (${[...new Set(pres.map((x) => x.duvod))].join(", ")}).` : ""}`);
      setSleva(""); setMarze("");
      onHotovo();
    } catch (e) { setZprava(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const CIP = "tap md:min-h-0 h-10 md:h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 t-meta font-700 whitespace-nowrap disabled:opacity-50";
  const VSTUP = "amount w-16 h-10 md:h-8 text-[16px] md:text-[12.5px] text-center bg-white/10 rounded-full outline-none text-white placeholder:text-muted-3";
  const VYBER = "h-10 md:h-8 max-w-[180px] px-3 rounded-full bg-white/10 text-white text-[16px] md:text-[12.5px] font-600 outline-none";
  const potvrdit = (hodnota, zmena) => hodnota != null && (
    <button type="button" disabled={busy} onClick={() => proved(zmena)} aria-label="Použít"
      className="tap md:min-h-0 h-10 w-10 md:h-8 md:w-8 inline-flex items-center justify-center rounded-full bg-white text-ink disabled:opacity-50">
      <Check size={16} strokeWidth={2.6} />
    </button>
  );
  const s = cisloPct(sleva), m = cisloPct(marze);
  return (
    <div className="sticky z-30 bottom-[calc(84px+env(safe-area-inset-bottom))] md:bottom-4 mt-3" data-testid="cenik-hromadna-lista">
      <div className="rounded-[18px] bg-ink text-white shadow-panel px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2.5">
        <span className="flex items-center gap-3 w-full md:w-auto">
          <span className="t-telo font-800 whitespace-nowrap" data-testid="cenik-hromadna-pocet">Vybráno {ids.length}</span>
          {ids.length < celkem && (
            <button type="button" onClick={onVse} data-testid="cenik-hromadna-vse"
              className="tap md:min-h-0 t-meta font-700 underline underline-offset-2 hover:text-white/80">Vybrat vše ({celkem})</button>
          )}
          <button type="button" onClick={onZrusit} className="tap md:min-h-0 ml-auto md:hidden t-meta font-700 text-muted-3">Zrušit výběr</button>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="t-meta text-muted-3" title="Sleva od dodavatele: prodej zůstane, nákup = prodej − sleva">Sleva</span>
          <input value={sleva} onChange={(e) => setSleva(e.target.value)} inputMode="decimal" placeholder="%" aria-label="Sleva od dodavatele v %"
            onKeyDown={(e) => e.key === "Enter" && s != null && proved({ sleva_pct: s })} className={VSTUP} data-testid="cenik-hromadna-sleva" />
          {potvrdit(s, { sleva_pct: s })}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="t-meta text-muted-3" title="Marže = přirážka k nákupu: nákup zůstane, prodej = nákup + marže">Marže</span>
          <input value={marze} onChange={(e) => setMarze(e.target.value)} inputMode="decimal" placeholder="%" aria-label="Marže v %"
            onKeyDown={(e) => e.key === "Enter" && m != null && proved({ marze_pct: m })} className={VSTUP} data-testid="cenik-hromadna-marze" />
          {potvrdit(m, { marze_pct: m })}
        </span>
        <select className={VYBER} value="" disabled={busy} onChange={(e) => e.target.value && proved({ vyrobce_id: e.target.value === "-" ? "" : e.target.value })}
          aria-label="Výrobce" data-testid="cenik-hromadna-vyrobce">
          <option value="">Výrobce…</option>
          {vyrobci.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          <option value="-">bez výrobce</option>
        </select>
        <select className={VYBER} value="" disabled={busy} onChange={(e) => e.target.value && proved({ kategorie_id: e.target.value === "-" ? "" : e.target.value })}
          aria-label="Technologie" data-testid="cenik-hromadna-technologie">
          <option value="">Technologie…</option>
          {kategorie.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
          <option value="-">bez technologie</option>
        </select>
        <select className={VYBER} value="" disabled={busy} onChange={(e) => e.target.value && proved({ typ: e.target.value })}
          aria-label="Typ" data-testid="cenik-hromadna-typ">
          <option value="">Typ…</option>
          {Object.entries(TYP_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <span className="flex flex-wrap items-center gap-1.5">
          {VYZNAM.map((v) => (
            <button key={v.klic} type="button" disabled={busy} onClick={() => proved({ priority: v.klic })} title={v.titul}
              data-testid={`cenik-hromadna-vyznam-${v.klic}`} className={CIP}>{v.titul}</button>
          ))}
        </span>
        <button type="button" onClick={onZrusit} data-testid="cenik-hromadna-zrusit"
          className="hidden md:inline-flex md:ml-auto t-meta font-700 text-muted-3 hover:text-white">Zrušit výběr</button>
        {zprava && <span className="basis-full t-meta text-white/80" data-testid="cenik-hromadna-zprava">{zprava}</span>}
      </div>
    </div>
  );
}
